import { Alert, Button, Checkbox, Field, Icons, Input } from "@dms/ui";
import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { ApiError } from "../lib/apiClient";
import { safeInternalPath } from "../lib/safeReturn";

// Екран входу (ClearFlow): split brand-panel + форма. Стани initial/loading/error
// (AUTH_INVALID_CREDENTIALS, AUTH_ACCOUNT_LOCKED, AUTH_2FA_REQUIRED).
export function LoginPage() {
  const { login, status } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const returnTo = safeInternalPath(params.get("returnTo"), "/dashboard");

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [needTotp, setNeedTotp] = useState(false);
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (status === "authenticated") {
      navigate(returnTo, { replace: true });
    }
  }, [status, returnTo, navigate]);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(username, password, needTotp ? totpCode : undefined);
      navigate(returnTo, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === "AUTH_2FA_REQUIRED") {
        setNeedTotp(true);
        setError("Введіть код двофакторної автентифікації");
      } else if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Не вдалося увійти. Спробуйте ще раз.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page" style={{ display: "flex", height: "100vh", minHeight: 0, background: "var(--neutral-0)" }}>
      {/* Brand panel */}
      <div
        className="login-page__brand"
        style={{
          flex: 1,
          // Фонове зображення cover.png із синім брендовим оверлеєм для читабельності тексту.
          backgroundImage:
            "linear-gradient(155deg, rgba(13,42,92,0.62), rgba(7,24,56,0.78)), url(/brand/cover.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          color: "#fff",
          padding: 48,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div className="login-page__orb" style={{ position: "absolute", right: -120, top: -80, width: 420, height: 420, borderRadius: "50%", background: "radial-gradient(circle, rgba(24,144,168,0.35), transparent 70%)" }} />
        <div className="login-page__orb" style={{ position: "absolute", left: -100, bottom: -120, width: 360, height: 360, borderRadius: "50%", background: "radial-gradient(circle, rgba(24,144,168,0.22), transparent 70%)" }} />
        <img className="login-page__logo" src="/brand/clearflow-logo-horizontal-white.png" alt="ClearFlow" style={{ height: 52, objectFit: "contain", alignSelf: "flex-start", position: "relative" }} />
        <div className="login-page__intro" style={{ position: "relative", maxWidth: 420 }}>
          <h1 style={{ fontSize: 34, lineHeight: "42px", fontWeight: "var(--fw-bold)", letterSpacing: "var(--ls-tight)", margin: 0 }}>
            Система електронного документообігу
          </h1>
          <p style={{ fontSize: "var(--fs-body-lg)", lineHeight: "var(--lh-body-lg)", color: "rgba(255,255,255,0.78)", marginTop: 14 }}>
            Реєстрація, погодження, контроль виконання та архівування документів — в єдиному захищеному середовищі.
          </p>
        </div>
        <div className="login-page__trust" style={{ position: "relative", display: "flex", gap: 24, fontSize: "var(--fs-body-sm)", color: "rgba(255,255,255,0.7)" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Icons.lock size={15} /> Захищений доступ
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Icons.audit size={15} /> Повний аудит дій
          </span>
        </div>
      </div>

      {/* Form panel */}
      <div className="login-page__form-panel" style={{ width: 460, flex: "none", background: "var(--neutral-0)", display: "flex", alignItems: "center", justifyContent: "center", padding: 40 }}>
        <div className="login-page__form-card" style={{ width: "100%", maxWidth: 340 }}>
          <h2 style={{ fontSize: "var(--fs-h2)", fontWeight: "var(--fw-bold)", margin: "0 0 6px" }}>Вхід до системи</h2>
          <p style={{ fontSize: "var(--fs-body)", color: "var(--color-text-secondary)", margin: "0 0 28px" }}>
            Увійдіть за обліковим записом установи
          </p>
          <form className="login-page__form" onSubmit={onSubmit} noValidate style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {error !== null && <Alert tone="error">{error}</Alert>}
            <Field label="Імʼя користувача" htmlFor="login-username">
              <Input
                id="login-username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                iconLeft={<Icons.user size={16} />}
                required
              />
            </Field>
            <Field label="Пароль" htmlFor="login-password">
              <Input
                id="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                iconLeft={<Icons.lock size={16} />}
                required
              />
            </Field>
            {needTotp && (
              <Field label="Код 2FA" htmlFor="login-totp">
                <Input
                  id="login-totp"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value)}
                  inputMode="numeric"
                />
              </Field>
            )}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Checkbox checked={remember} onChange={() => setRemember((v) => !v)} label="Запам'ятати мене" />
              <a href="#" style={{ fontSize: "var(--fs-body-sm)" }}>
                Забули пароль?
              </a>
            </div>
            <Button type="submit" fullWidth size="lg" loading={submitting}>
              {submitting ? "Вхід…" : "Увійти"}
            </Button>
          </form>
          <p style={{ fontSize: "var(--fs-caption)", color: "var(--color-text-tertiary)", textAlign: "center", marginTop: 24 }}>
            Доступ лише для авторизованих користувачів установи. Усі дії журналюються.
          </p>
        </div>
      </div>
    </div>
  );
}
